import { i18n } from "@lingui/core";
import { FormControlMixin } from "@open-wc/form-control";
import {
	addDays,
	addMonths,
	differenceInCalendarDays,
	eachDayOfInterval,
	eachWeekOfInterval,
	endOfWeek,
	format,
	formatISO,
	getDate,
	getWeekOfMonth,
	isSameDay,
	isSameMonth,
	isToday,
	Locale,
	lastDayOfMonth,
	startOfMonth,
	startOfToday,
	startOfWeek,
	subDays,
	subMonths,
} from "date-fns";
import { da, enGB, fi, nb, sv } from "date-fns/locale";
import { html, LitElement, nothing, PropertyValues } from "lit";
import { property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { styleMap } from "lit/directives/style-map.js";

import "../icon/icon.js";
import "../tooltip/tooltip.js";

import { activateI18n, detectLocale } from "../i18n.js";
import { reset } from "../styles.js";

import { messages as daMessages } from "./locales/da/messages.mjs";
import { messages as enMessages } from "./locales/en/messages.mjs";
import { messages as fiMessages } from "./locales/fi/messages.mjs";
import { messages as nbMessages } from "./locales/nb/messages.mjs";
import { messages as svMessages } from "./locales/sv/messages.mjs";
import {
	inputHelpTextStyles,
	inputLabelStyles,
	wDatepickerStyles,
} from "./styles/w-datepicker.styles.js";
import { wDatepickerCalendarStyles } from "./styles/w-datepicker-calendar.styles.js";
import { wDatepickerDayStyles } from "./styles/w-datepicker-day.styles.js";
import { wDatepickerMonthStyles } from "./styles/w-datepicker-month.styles.js";
import {
	fromISOToDate,
	getDateInputDisplayValue,
	getDateInputPlaceholder,
	getDateInputType,
} from "./utils.js";

const inputType = getDateInputType();

const calendarId = "calendar";
const inputId = "input";
const toggleButtonId = "toggle";
const wrapperId = "wrapper";
const previousMonthButtonId = "previous";

// Convenience to support the common locales of our users.
// For other locales either add to this list or point users
// to https://date-fns.org/v4.1.0/docs/setDefaultOptions
const datefnsLocale = {
	en: enGB,
	nb,
	sv,
	da,
	fi,
};

/**
 * A date picker allows the user to select a specific calendar date.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/date-picker/frameworks/elements)
 */
class WarpDatepicker extends FormControlMixin(LitElement) {
	static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	static styles = [
		reset,
		inputLabelStyles,
		inputHelpTextStyles,
		wDatepickerStyles,
		wDatepickerCalendarStyles,
		wDatepickerDayStyles,
		wDatepickerMonthStyles,
	];

	/**
	 * The label displayed above the date input.
	 *
	 * Use this to give the datepicker a visible and accessible name.
	 */
	@property({ reflect: true })
	label: string | undefined;

	/**
	 * Whether user input is required on the input before form submission
	 */
	@property({ type: Boolean, reflect: true })
	required = false;

	/**
	 * Indicate visually that the field is optional
	 */
	@property({ type: Boolean, reflect: true })
	optional = false;

	/**
	 * Use in combination with `invalid` to show as a validation error message,
	 * or on its own to show a help text.
	 *
	 * @summary Description shown below the input field
	 */
	@property({ type: String, reflect: true, attribute: "help-text" })
	helpText: string | undefined;

	/**
	 * Mark the form field as invalid.
	 *
	 * Make sure to also set a `help-text` to help users fix the validation problem.
	 */
	@property({ type: Boolean, reflect: true })
	invalid = false;

	/**
	 * Supplementary information that should show in a tooltip behind an information icon after the label.
	 *
	 * You must provide a label to be able to show an info icon with a tooltip.
	 */
	@property({ type: String, reflect: true })
	tooltip?: string;

	/**
	 * The locale used for calendar labels and date formatting.
	 *
	 * This takes precedence over the `<html>` `lang` attribute. Supported built-in locales are `en`, `nb`, `sv`, `da`, and `fi`.
	 */
	@property({ reflect: true })
	lang!: string;

	/**
	 * The name submitted with the date value.
	 *
	 * Use this when the datepicker belongs to a form and its value should be included in form data.
	 */
	@property({ reflect: true })
	name: string | undefined;

	/**
	 * The selected date value.
	 *
	 * Use an ISO date string in `YYYY-MM-DD` format. The value is submitted with the form and is reset to its initial value when the form resets.
	 */
	@property({ reflect: true })
	value: string | undefined;

	/**
	 * Keep in mind that using disabled in its current form is an anti-pattern.
	 *
	 * There will always be users who don't understand why an element is disabled, or users who can't even see that it is disabled because of poor lighting conditions or other reasons.
	 *
	 * Please consider more informative alternatives before choosing to use disabled on an element.
	 *
	 * @summary Makes the element not focusable and hides it from form submits
	 */
	@property({ type: Boolean, reflect: true })
	disabled = false;

	/**
	 * Whether the input can be selected but not changed by the user
	 */
	@property({ type: Boolean, reflect: true })
	readonly = false;

	/**
	 * The date format used in the calendar header.
	 *
	 * The syntax is defined by [date-fns/format](https://date-fns.org/v4.1.0/docs/format).
	 */
	@property({ attribute: "header-format" })
	headerFormat = "MMMM yyyy";

	/**
	 * The weekday format shown above the calendar grid.
	 *
	 * The syntax is defined by [date-fns/format](https://date-fns.org/v4.1.0/docs/format).
	 */
	@property({ attribute: "weekday-format" })
	weekdayFormat = "EEEEEE";

	/**
	 * Function used to disable dates in the calendar.
	 *
	 * Set this on the element instance in JavaScript, not as an HTML attribute. Disabled dates cannot be selected from the calendar.
	 *
	 * @example
	 * ```ts
	 * import type { WarpDatepicker } from "@warp-ds/elements";
	 * import { isBefore, startOfDay } from 'date-fns';
	 *
	 * const today = startOfDay(new Date());
	 * const datePicker = document.querySelector('w-datepicker') as WarpDatepicker;
	 * datePicker.isDayDisabled = (day: Date) => isBefore(startOfDay(day), today);
	 * ```
	 */
	@property({ attribute: false })
	isDayDisabled: ((day: Date) => boolean) | undefined;

	/**
	 * The date format used for calendar day accessible names.
	 *
	 * The syntax is defined by [date-fns/format](https://date-fns.org/v4.1.0/docs/format).
	 */
	@property({ attribute: "day-format" })
	dayFormat = "PPPP";

	@state()
	isCalendarOpen = false;

	@state()
	navigationDate: Date = startOfToday();

	/** @internal This gets picked up by the custom element manifest analyzer as a property for some reason */
	@state()
	locale: Locale = enGB;

	@state()
	get selectedDate(): Date | null {
		if (!this.value) return null;
		return fromISOToDate(this.value) ?? null;
	}

	@state()
	get month() {
		return startOfMonth(this.navigationDate);
	}

	// capture the initial value using connectedCallback and #initialValue
	#initialValue: string | undefined = undefined;

	@state()
	get weeks() {
		const month = this.month;
		const navigationDate = this.navigationDate;
		const weekStarts = eachWeekOfInterval(
			{
				start: startOfMonth(month),
				end: lastDayOfMonth(month),
			},
			{ locale: this.locale },
		);
		const weekIntervals = weekStarts
			.map((weekStart) => ({
				start: weekStart,
				end: endOfWeek(weekStart, { locale: this.locale }),
			}))
			.map((week) => eachDayOfInterval(week));

		// Inserts the navigation date into the date matrix
		if (isSameMonth(navigationDate, month)) {
			const weekOfMonth =
				getWeekOfMonth(navigationDate, { locale: this.locale }) - 1;

			// we need to get the day of the week to index into the correct day after we've gotten the week
			// getDay() however isn't locale aware. This is a good replacement
			const day = differenceInCalendarDays(
				navigationDate,
				startOfWeek(navigationDate, { locale: this.locale }),
			);

			weekIntervals[weekOfMonth][day] = navigationDate;
		}
		return weekIntervals;
	}

	@query(`#${calendarId}`)
	calendar!: HTMLDivElement;

	@query(`#${inputId}`, true)
	input!: HTMLInputElement;

	@query(`#${toggleButtonId}`, true)
	toggleButton!: HTMLButtonElement;

	@query(`#${wrapperId}`, true)
	wrapper!: HTMLDivElement;

	/**
	 * This is the first focusable element, needed for the modal focus trap.
	 *
	 * Don't cache this and other `@query` fields from inside the calendar modal.
	 * They work the first time, but once the calendar is closed and reopened
	 * the query will point to an element that doesn't exist anymore.
	 */
	@query(`#${previousMonthButtonId}`)
	previousMonthButton!: HTMLButtonElement;

	@query('[aria-current="date"]')
	todayCell!: HTMLTableCellElement;

	@query('[data-navigation="true"]')
	selectedCell!: HTMLTableCellElement;

	get #inputLocale() {
		return this.lang || detectLocale() || "en";
	}

	get #inputValue() {
		if (inputType === "text" && this.value) {
			return getDateInputDisplayValue(this.value, this.#inputLocale);
		}

		return this.value || "";
	}

	async #toggleCalendarOpen(e: MouseEvent | KeyboardEvent) {
		e.preventDefault();
		this.isCalendarOpen = !this.isCalendarOpen;
		if (this.isCalendarOpen) {
			// Move into the calendar and announce the availability
			// of the calendar grid to screen reader users.
			await this.updateComplete;
			(this.selectedCell || this.todayCell).focus();
		}
	}

	#nextMonth() {
		this.navigationDate = addMonths(this.month, 1);
	}

	#previousMonth() {
		this.navigationDate = subMonths(this.month, 1);
	}

	#customValidationMessage = "";

	async #dispatchChangeEvent() {
		// Let Lit finish rendering the updated value for the input field so
		// the `event.target.value` is correct.
		await this.updateComplete;
		this.input.dispatchEvent(
			new Event("change", { bubbles: true, composed: true }),
		);
	}

	/**
	 * We can't use private fields (`#` prefix) for this method
	 * since we can't overwrite private field methods. We need
	 * to `.bind(this)` in the constructor because we need one
	 * stable method handler we can register and unregister on
	 * `document`, that has access to this specific instance
	 * of WarpDatepicker to control the calendar.
	 * @internal
	 */
	private _onClickOutside(e: MouseEvent | FocusEvent | TouchEvent) {
		if (!this.isCalendarOpen) {
			return;
		}

		const isInsideDatePicker = this.contains(e.target as Node);
		if (isInsideDatePicker) {
			return;
		}

		this.isCalendarOpen = false;
	}

	#onInput(e: InputEvent) {
		this.value = (e.target as HTMLInputElement).value;
	}

	#onInputBlur() {
		this.#hasInteracted = true;
		this.#updateValidity();
	}

	#onInputClick(e: PointerEvent) {
		// stop Safari on macOS from showing the native calendar
		e.preventDefault();
		this.isCalendarOpen = true;
	}

	#onInputKeyDown(e: KeyboardEvent) {
		if (e.key === " ") return this.#toggleCalendarOpen(e);
		if (e.key === ",") {
			e.preventDefault();
		}
		if (e.key === "Enter" && this.internals.form) {
			(this.internals.form as HTMLFormElement).requestSubmit();
		}
	}

	async #onCalendarKeyDown(e: KeyboardEvent) {
		const navigationDate = this.navigationDate;
		let newNavigationDate: Date | null = null;

		switch (e.key) {
			case "ArrowUp":
				newNavigationDate = subDays(navigationDate, 7);
				break;
			case "ArrowLeft":
				newNavigationDate = subDays(navigationDate, 1);
				break;
			case "Home":
				newNavigationDate = startOfWeek(navigationDate, {
					locale: this.locale,
				});
				break;
			case "PageUp":
				newNavigationDate = subMonths(navigationDate, 1);
				break;
			case "ArrowDown":
				newNavigationDate = addDays(navigationDate, 7);
				break;
			case "ArrowRight":
				newNavigationDate = addDays(navigationDate, 1);
				break;
			case "End":
				newNavigationDate = endOfWeek(navigationDate, { locale: this.locale });
				break;
			case "PageDown":
				newNavigationDate = addMonths(navigationDate, 1);
				break;
			case "Escape":
				this.isCalendarOpen = false;
				break;
			case "Tab":
				if ((e.target as HTMLElement).tagName === "TD" && !e.shiftKey) {
					// If Tab and no Shift we are about to leave the modal and should move focus
					// to the Previous month button. If Shift is held, proceed as normal.
					e.preventDefault();
					this.previousMonthButton.focus();
				} else if (
					(e.target as HTMLElement).id === previousMonthButtonId &&
					e.shiftKey
				) {
					// If we're holding Shift and pressing Tab we are moving back in the focus
					// order. If we're about to leave the previous month button (the first focusable
					// element in the modal), move the focus to the last focusable element, which
					// is the current navigation date in the calendar grid.
					e.preventDefault();
					(this.selectedCell || this.todayCell).focus();
				}
				break;
		}

		if (newNavigationDate) {
			// prevent scrolling the viewport
			e.preventDefault();
			this.navigationDate = newNavigationDate;

			// move keyboard focus once Lit has rendered an updated `data-navigation`
			// based on this.navigationDate
			await this.updateComplete;
			this.selectedCell.focus();
		}
	}

	#onCalendarSelect(event: MouseEvent | KeyboardEvent) {
		// Clicks can hit the `<div>` inside the `<td>`, so look at
		// currentTarget (where the listener is registered) to get
		// the `<td>` consistently.
		const isoDate = (event.currentTarget as HTMLTableCellElement).dataset.date;
		if ("key" in event) {
			if (event.key === "Enter" || event.key === " ") {
				// Prevents whitespace from being added to the input field
				event.preventDefault();
				this.value = isoDate;
				this.input.value = this.#inputValue;
				this.#updateValidity();
				this.isCalendarOpen = false;
				this.toggleButton.focus();
				this.#dispatchChangeEvent();
			}
		} else {
			this.value = isoDate;
			this.input.value = this.#inputValue;
			this.#updateValidity();
			this.isCalendarOpen = false;
			this.#dispatchChangeEvent();
		}
	}

	// Track whether the current invalid/helpText state was set by validation
	#validationActive = false;

	// Store the original helpText to restore when validation passes
	#originalHelpText: string | undefined = undefined;

	// Track whether the user has interacted with the field
	#hasInteracted = false;

	constructor() {
		super();

		activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);

		const lang = detectLocale();
		if (lang && datefnsLocale[lang]) {
			this.locale = datefnsLocale[lang];
		}

		this._onClickOutside = this._onClickOutside.bind(this);
	}

	resetFormControl(): void {
		this.value = this.#initialValue;
		this.#hasInteracted = false;
		this.#clearValidationState();
		this.#updateValidity();
	}

	/** Returns the validation message if the textarea is invalid, otherwise an empty string */
	get validationMessage(): string {
		return this.internals.validationMessage;
	}

	/** Returns the validity state of the textarea */
	get validity(): ValidityState {
		return this.internals.validity;
	}

	/** @internal */
	get _error() {
		if (this.invalid && this.helpText) return this.helpText;
		return undefined;
	}

	/** Checks whether the textarea passes constraint validation */
	checkValidity(): boolean {
		this.#updateValidity();
		return this.internals.checkValidity();
	}

	/** Checks validity and shows the browser's validation message if invalid */
	reportValidity(): boolean {
		this.#hasInteracted = true;
		this.#updateValidity();
		return this.internals.checkValidity();
	}

	/** Sets a custom validation message. Pass an empty string to clear. */
	setCustomValidity(message: string): void {
		this.#customValidationMessage = message;

		if (message) {
			this.#updateValidity();
			this.#setValidationState(message);
		} else {
			this.#clearValidationState();
			this.#updateValidity();
		}
	}

	/** @internal */
	#setValidationState(message: string): void {
		if (!this.#validationActive) {
			this.#originalHelpText = this.helpText;
		}
		this.#validationActive = true;
		this.invalid = true;
		this.helpText = message;
	}

	/** @internal */
	#clearValidationState(): void {
		if (this.#validationActive) {
			this.invalid = false;
			this.helpText = this.#originalHelpText;
			this.#originalHelpText = undefined;
			this.#validationActive = false;
		}
	}

	/** @internal */
	#handleInvalid = (e: Event) => {
		// Prevent browser's native validation bubble
		e.preventDefault();
		// Mark as interacted and show validation state
		this.#hasInteracted = true;
		this.#updateValidity();
	};

	/** @internal */
	#updateValidity(): void {
		if (this.disabled) {
			this.internals.setValidity({});
			this.#clearValidationState();
			return;
		}

		const flags: ValidityStateFlags = {
			valueMissing: this.required && !this.value,
			customError: this.#customValidationMessage !== "",
		};

		if (Object.values(flags).some(Boolean)) {
			const message = flags.customError
				? this.#customValidationMessage
				: this.input?.validationMessage || "";

			this.internals.setValidity(flags, message, this.input);

			if (this.#hasInteracted) {
				this.#setValidationState(message);
			}

			return;
		}

		this.internals.setValidity({});
		this.#clearValidationState();
	}

	connectedCallback(): void {
		super.connectedCallback();

		this.#initialValue = this.value;
		if (typeof this.value !== "undefined") {
			this.setValue(this.value);
		}

		// Listen for invalid event on the host element (fired by form validation)
		this.addEventListener("invalid", this.#handleInvalid);

		// Local lang attribute takes precedence
		const lang = this.lang;
		// @ts-expect-error We're defensive here in the lookup
		if (lang && datefnsLocale[lang]) {
			// @ts-expect-error We're defensive here in the lookup
			this.locale = datefnsLocale[lang];
		}

		if (this.value) {
			const iso = fromISOToDate(this.value);
			if (iso) {
				this.navigationDate = iso;
			}
		}

		document.addEventListener("mousedown", this._onClickOutside);
		document.addEventListener("touchend", this._onClickOutside);
		document.addEventListener("focusin", this._onClickOutside);
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();

		this.removeEventListener("invalid", this.#handleInvalid);
		document.removeEventListener("mousedown", this._onClickOutside);
		document.removeEventListener("touchend", this._onClickOutside);
		document.removeEventListener("focusin", this._onClickOutside);
	}

	updated(changedProperties: Map<string, unknown>): void {
		if (changedProperties.has("value") && typeof this.value !== "undefined") {
			this.setValue(this.value);
		}
		if (
			changedProperties.has("value") ||
			changedProperties.has("required") ||
			changedProperties.has("disabled")
		) {
			this.#updateValidity();
		}
	}

	firstUpdated(changedProperties: PropertyValues<this>) {
		super.firstUpdated(changedProperties);
		// Initialize validity after the shadow DOM is ready
		this.#updateValidity();
	}

	render() {
		return html`
			<div class="w-datepicker-wrapper" id="${wrapperId}">
				<label class="w-datepicker-input-label" for="${inputId}">
					${this.label}
					${
						this.optional && !this.required
							? html`
									<span>
										${i18n._({
											id: "datepicker.label.optional",
											message: "Optional",
											comment: "Shown behind label when marked as optional",
										})}
									</span>
								`
							: nothing
					}
					${
						this.tooltip
							? html`
									<button
										id="tooltip-target"
										part="tooltip-target"
										aria-describedby="tooltip"
									>
										<w-icon name="Info" size="small"></w-icon>
									</button>
									<w-tooltip
										for="tooltip-target"
										id="tooltip"
										exportparts="tooltip, arrow, beak, hover-bridge"
									>
										${this.tooltip}
									</w-tooltip>
								`
							: nothing
					}
				</label>
				<div class="w-datepicker-input-wrapper">
					<input
						id="${inputId}"
						type="${inputType}"
						name="${ifDefined(this.name)}"
						placeholder="${ifDefined(
							inputType === "text"
								? getDateInputPlaceholder(this.#inputLocale)
								: undefined,
						)}"
						.value="${this.#inputValue}"
						class="w-datepicker-input"
						?required=${this.required}
						?disabled=${this.disabled}
						?readonly=${this.readonly}
						aria-describedby="${ifDefined(
							this.helpText
								? "help-text"
								: this.ariaDescription
									? "aria-description"
									: undefined,
						)}"
						aria-errormessage="${ifDefined(
							this._error ? "help-text" : undefined,
						)}"
						aria-invalid=${ifDefined(this.invalid ? "true" : undefined)}
						@click="${this.#onInputClick}"
						@input="${this.#onInput}"
						@blur="${this.#onInputBlur}"
						@keydown="${this.#onInputKeyDown}"
					/>
					<w-button
						aria-label="${
							this.value
								? i18n.t({
										id: "datepicker.toggle.changeDate",
										values: { currentDate: format(this.value, this.dayFormat) },
										message: "Change date, {currentDate}",
										comment:
											"Used by screen readers to describe the button that toggles open the calendar in a date picker when there is a selected date",
									})
								: i18n.t({
										id: "datepicker.toggle.chooseDate",
										message: "Choose date",
										comment:
											"Used by screen readers to describe the button that toggles open the calendar in a date picker when there is no selected date",
									})
						}"
						aria-controls="${calendarId}"
						class="w-datepicker-button"
						data-testid="${toggleButtonId}"
						id="${toggleButtonId}"
						variant="utilityQuiet"
						?disabled=${this.disabled || this.readonly}
						type="button"
						@click="${this.#toggleCalendarOpen}"
					>
						<w-icon
							name="Calendar"
							size="small"
							locale="${detectLocale()}"
							class="flex"
						></w-icon>
					</w-button>
				</div>
				${
					this.helpText
						? html`<div id="help-text" class="help-text">${this.helpText}</div>`
						: nothing
				}
			</div>
			<div
				class="w-dropdown__popover w-dropdown__popover--open"
				style="${styleMap({
					display: this.isCalendarOpen ? undefined : "none",
				})}"
			>
				<div
					aria-label="${i18n.t({
						id: "datepicker.calendar.roleDescription",
						message: "Date picker",
						comment:
							"Used by screen readers to announce that the calendar element is a date picker.",
					})}"
					aria-modal="true"
					role="dialog"
					class="w-datepicker__calendar"
					data-testid="${calendarId}"
					id="${calendarId}"
					@keydown="${this.#onCalendarKeyDown}"
				>
					<div class="w-datepicker__month-nav">
						<w-button
							aria-label="${i18n.t({
								id: "datepicker.calendar.previousMonth",
								message: "Previous month",
								comment: "Screen reader label for the previous month button.",
							})}"
							class="w-datepicker__month__nav__button"
							id="${previousMonthButtonId}"
							data-testid="${previousMonthButtonId}"
							variant="utility"
							quiet
							small
							@click="${this.#previousMonth}"
						>
							<w-icon
								name="ChevronLeft"
								size="small"
								locale="${detectLocale()}"
								class="flex"
							></w-icon>
						</w-button>
						<div aria-live="polite" class="w-datepicker__month__nav__header">
							${format(this.month, this.headerFormat, { locale: this.locale })}
						</div>
						<w-button
							aria-label="${i18n.t({
								id: "datepicker.calendar.nextMonth",
								message: "Next month",
								comment: "Screen reader label for the next month button.",
							})}"
							class="w-datepicker__month__nav__button"
							data-testid="${calendarId}-next"
							variant="utility"
							quiet
							small
							@click="${this.#nextMonth}"
						>
							<w-icon
								name="ChevronRight"
								size="small"
								locale="${detectLocale()}"
								class="flex"
							></w-icon>
						</w-button>
					</div>
					<div class="w-datepicker__month">
						<table class="w-datepicker__table" role="grid">
							<thead class="w-datepicker__weekdays">
								<tr>
									${this.weeks[0].map(
										(day) =>
											html`<th class="w-datepicker__weekday">
												${format(day, this.weekdayFormat, {
													locale: this.locale,
												})}
											</th> `,
									)}
								</tr>
							</thead>
							<tbody>
								${this.weeks.map(
									(week) =>
										html`<tr>
											${week.map((day) => {
												if (!isSameMonth(this.month, day)) {
													return html`<td></td>`;
												}

												const isDisabled = this.isDayDisabled
													? this.isDayDisabled(day)
													: false;
												const isSelected =
													Boolean(this.selectedDate) &&
													isSameDay(day, this.selectedDate!);
												const isNavigationDate = day === this.navigationDate;

												return html`<td
													aria-current="${ifDefined(
														isToday(day) ? "date" : undefined,
													)}"
													aria-disabled="${isDisabled}"
													aria-label="${format(day, this.dayFormat, {
														locale: this.locale,
													})}"
													aria-selected="${isSelected}"
													class="${classMap({
														"w-datepicker__day": true,
														"w-datepicker__day--today": isToday(day),
														"w-datepicker__day--selected": isSelected,
														"w-datepicker__day--disabled": isDisabled,
														"w-datepicker__day--navigation": isNavigationDate,
													})}"
													data-navigation="${isNavigationDate}"
													data-date="${formatISO(day, {
														representation: "date",
													})}"
													role="gridcell"
													tabindex="${isNavigationDate ? 0 : -1}"
													@click="${
														isDisabled ? undefined : this.#onCalendarSelect
													}"
													@keydown="${
														isDisabled ? undefined : this.#onCalendarSelect
													}"
												>
													<div>${getDate(day)}</div>
												</td>`;
											})}
										</tr>`,
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"w-datepicker": WarpDatepicker;
	}
}

if (!customElements.get("w-datepicker")) {
	customElements.define("w-datepicker", WarpDatepicker);
}

export { WarpDatepicker };
